<template>
  <div class="hidden xl:flex xl:gap-x-5">
    <NavLogo show-country-flag />

    <!-- Desktop nav links -->
    <ul class="hidden xl:grow xl:flex xl:gap-x-[1.03rem] 2xl:gap-x-8 xl:items-center">
      <template v-for="(navLink, index) in navLinkList.filter(link => link.label != null && link.url != null)">
        <DesktopNavMegaMenuButton v-if="navLink.megaMenuOptions?.enabled" 
                                  ref="megaMenus"
                                  :id="`desktop-nav-link-${index}`" 
                                  @opened="closeOpenMegaMenus(index)"
                                  v-bind="navLink" />
        <NavMenuLink v-else :id="`desktop-nav-link-${index}`" v-bind="navLink" />
      </template>
    </ul>

    <div class="flex justify-center items-center xl:gap-x-5">
      <NavSearchBar show-taia-tooltip />

      <!-- Desktop Account Options -->
      <div class="hidden relative rounded-t-md bg-white xl:block"
        :class="{ 'drop-shadow-md': isAccountOptionsMenuActive }" v-if="isLoggedIn">

        <button class="m-3 w-9 h-9 rounded-full bg-linear-to-br from-[#e45dbf] to-orange-500"
                aria-label="Account options menu"
                @click="toggleAccountOptionsMenu"
                v-click-outside="closeAccountOptionsMenu" >
            <span class="text-white font-bold leading-snug">
                {{ userInitials }}
            </span>
        </button>
        <AccountMenu :account-menu-option-list="accountMenuOptionList" :is-visible="isAccountOptionsMenuActive" />
      </div>
      <NuxtLink
          class="hidden rounded-md border border-gray-300 focus:text-gold-100 focus:border-gray-600 hover:text-gold-100 hover:border-gray-600 transition-colors duration-200 ease-in-out my-3 px-4 xl:flex xl:items-center pt-[8.75px] pb-[8.75px] whitespace-nowrap"
          :to="{ path: '/login', query: { returnTo: route.fullPath } }"
          external
          v-else
        >
        Sign In
      </NuxtLink>

      <NavCartButton show-add-to-cart-toast />
    </div>
  </div>
</template>

<script setup lang="ts">
import AccountMenu, { type AccountOption } from "../molecule/accountMenu.vue"
import NavMenuLink from "../atom/navMenuLink.vue"
import NavLogo from '../atom/navLogo.vue'
import NavSearchBar from '../atom/navSearchBar.vue';
import DesktopNavMegaMenuButton from '../atom/desktopNavMegaMenuButton.vue'
import { type BuilderNavLink } from '../organism/navBar.vue'
import NavCartButton from '../atom/navCartButton.vue'
import { faHeart } from '@fortawesome/pro-regular-svg-icons'

interface DesktopNavMenuProps {
  navLinkList?: BuilderNavLink[]
  isLoggedIn?: boolean
  cartCount?: number
  userInitials?: string
  userAuthorities?: string[]
}

const {
  navLinkList = [],
  isLoggedIn = false,
  userInitials = '',
  userAuthorities = [],
} = defineProps<DesktopNavMenuProps>();

const route = useRoute();
const megaMenus = useTemplateRefsList<typeof DesktopNavMegaMenuButton>();
const isAccountOptionsMenuActive = ref(false);
const toggleAccountOptionsMenu = () => isAccountOptionsMenuActive.value = !isAccountOptionsMenuActive.value;
const closeAccountOptionsMenu = () => isAccountOptionsMenuActive.value = false;
const hasRole = (role: string) => userAuthorities.includes(role)
const accountMenuOptionList: AccountOption[] = [
  { label: "Profile", path: "/account/profile", icon: 'user' },
  { label: "Favorites", path: "/account/favorites", icon: faHeart },  {
    label: "Dispensary",
    path: "/account/dispensary",
    icon: 'briefcase-medical',
    show: () => hasRole("ROLE_PROFESSIONAL")
  },
  { label: "Integrations", path: "/account/developer", icon: 'link', show: () => hasRole("ROLE_DEVELOPER") },
  { label: "Storefront", path: "/account/storefront", icon: 'store', show: () => hasRole("ROLE_AMBASSADOR") },
  { label: "Quick Order", path: "/quick-order", icon: 'rabbit-fast' },
  { label: "Track Order", path: "/account/orders", icon: 'barcode' },
  { label: "Settings", path: "/account/settings/personal", icon: 'cog' },
  { label: "Sign Out", path: "/logout", icon: 'arrow-right-from-bracket' },
]

const closeOpenMegaMenus = (openedIndex: number) => {
  if (!megaMenus.value) return;
  for (let i = 0; i < megaMenus.value.length; i++) {
    const megaMenu = megaMenus.value[i];
    if (!!megaMenu && i !== openedIndex) {
      megaMenu.close();
    }
  }
}

</script>

<style scoped>
@reference "~/assets/css/product-listing.css";
@reference "~/assets/css/main.css";
@reference "~/assets/css/product-detail.css";
</style>
